import type { CriterionProgress, TierStatus } from "../types";
import type { RebateTierTranslatedContent } from "../useRebateTierTranslatedContent";

import { Typo } from "../../Typo";
import { formatDateWithUtcOffset } from "../../utils/date";
import {
  currentMonthRemaining,
  formatCriterionValue,
  progressRatio,
} from "../formatVolume";
import { SheetNotice } from "./SheetNotice";

export type CriteriaExplainerCardProps = {
  progress: CriterionProgress;
  status: TierStatus;
  /** Rendered only when the transport carries an evaluation timestamp. */
  lastUpdated: string | null;
  content: RebateTierTranslatedContent;
};

/**
 * Month swatch shades run oldest → current: the two historic months use the
 * blue accent tints and the current month the solid brand blue.
 */
const MONTH_SWATCH = [
  "bg-surface-accent-blue-subtler",
  "bg-surface-accent-blue-subtle",
];
const CURRENT_MONTH_SWATCH = "bg-surface-brand-default";

/** "2026-08" → "Aug 2026". */
const formatMonth = (month: string): string => {
  const [year, index] = month.split("-").map(Number);
  if (!year || !index) return month;
  return new Date(Date.UTC(year, index - 1, 1)).toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

/**
 * Criteria explainer card — how a criterion is measured, plus the per-month
 * breakdown of the 3-month qualifying window.
 *
 * Chrome-free on purpose: `CriteriaExplainerSheet` wraps this in
 * `ResponsiveModal` for the "View criteria" entry point from `CycleMobileSheet`,
 * and `CriterionBar`'s info-icon `PopoverMenu` renders it directly as inline
 * overlay content — the two triggers share this one body rather than each
 * hand-rolling their own copy of it.
 */
export const CriteriaExplainerCard = ({
  progress,
  status,
  lastUpdated,
  content,
}: CriteriaExplainerCardProps) => {
  const notice =
    status === "grace"
      ? content.graceNotice
      : status === "frozen"
        ? content.frozenNotice
        : null;

  const lastMonthIndex = progress.months.length - 1;
  // Only trading volume is expressed in millions; client counts are bare numbers.
  const unitSuffix = progress.criterion === "tv" ? ` ${content.million}` : "";
  const explainerBody =
    progress.criterion === "tv"
      ? content.criteriaExplainerBody
      : progress.criterion === "ac"
        ? content.activeClientsTooltip
        : content.newClientsTooltip;
  const targetLabel =
    progress.criterion === "tv"
      ? content.threeMonthTarget
      : content.threeMonthClientTarget;

  const isActiveClients = progress.criterion === "ac";
  const currentValue = formatCriterionValue(
    progress.criterion,
    progress.current,
  );
  const targetValue = formatCriterionValue(progress.criterion, progress.target);
  const segmentWidths = progress.months.reduce<number[]>((widths, row) => {
    const consumed = widths.reduce((sum, width) => sum + width, 0);
    widths.push(
      Math.min(progressRatio(row.value, progress.target), 1 - consumed),
    );
    return widths;
  }, []);
  const remaining = currentMonthRemaining(progress);

  return (
    <div className="flex flex-col gap-6">
      {notice && <SheetNotice message={notice} />}

      <Typo element="p" type="bodySM" textColor="text-text-primary">
        {explainerBody}
      </Typo>

      <div className="flex flex-col gap-1">
        <Typo element="h3" type="labelSM" textColor="text-text-primary">
          {content.currentProgress}
        </Typo>
        {lastUpdated && (
          <Typo element="p" type="bodyXS" textColor="text-text-secondary">
            {content.dataLastUpdated(
              formatDateWithUtcOffset(new Date(lastUpdated)),
            )}
          </Typo>
        )}
      </div>

      {isActiveClients ? (
        <div className="flex flex-col gap-1">
          <Typo element="span" type="bodySM" textColor="text-text-secondary">
            {targetLabel}
          </Typo>
          <div className="flex items-baseline gap-0.5">
            <Typo element="span" type="labelXL" textColor="text-text-primary">
              {currentValue}
            </Typo>
            <Typo element="span" type="bodySM" textColor="text-text-secondary">
              {content.activeClientsTargetSuffix(targetValue)}
            </Typo>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex h-2 w-full overflow-hidden rounded bg-surface-tonal-default">
            {progress.months.map((row, index) => (
              <div
                key={row.month}
                className={`h-full ${index === lastMonthIndex ? CURRENT_MONTH_SWATCH : (MONTH_SWATCH[index] ?? MONTH_SWATCH[0])}`}
                style={{ width: `${(segmentWidths[index] ?? 0) * 100}%` }}
              />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Typo element="span" type="bodySM" textColor="text-text-secondary">
              {targetLabel}
            </Typo>
            <Typo element="span" type="bodySM" textColor="text-text-primary">
              {currentValue}
              <span className="text-text-secondary">
                {progress.criterion === "nc"
                  ? content.newClientsTargetSuffix(targetValue)
                  : `/${targetValue}${unitSuffix}`}
              </span>
            </Typo>
          </div>
        </div>
      )}

      {!isActiveClients && (
        <div className="flex flex-col gap-3">
          {progress.months.map((row, index) => {
            const isCurrent = index === lastMonthIndex;
            const rowValue = formatCriterionValue(
              progress.criterion,
              row.value,
            );
            return (
              <div
                key={row.month}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={`size-4 shrink-0 rounded ${isCurrent ? CURRENT_MONTH_SWATCH : (MONTH_SWATCH[index] ?? MONTH_SWATCH[0])}`}
                  />
                  <Typo
                    element="span"
                    type="bodySM"
                    textColor="text-text-secondary"
                  >
                    {isCurrent ? content.currentMonth : formatMonth(row.month)}
                  </Typo>
                </div>
                <Typo
                  element="span"
                  type="bodySM"
                  textColor="text-text-primary"
                >
                  {isCurrent && remaining !== null ? (
                    <>
                      {rowValue}
                      <span className="text-text-secondary">
                        {`/${formatCriterionValue(progress.criterion, remaining)}${unitSuffix}`}
                      </span>
                    </>
                  ) : (
                    `${rowValue}${unitSuffix}`
                  )}
                </Typo>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
